import { m } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, Shield, HardHat, CheckCircle, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import FormEmbed from "@/components/FormEmbed";
import CountUp from "@/components/CountUp";
import ResponsiveImage from "@/components/ResponsiveImage";

/** Every tile in the stat bar links somewhere, so each one earns its hover. */
const badges = [
  { icon: Shield, label: "Registered NJ LLC", to: "/about" },
  { icon: Star, label: "Owner-Led Crew", to: "/about" },
  { icon: Clock, label: "Mon – Sat", to: "/contact" },
  { icon: CheckCircle, label: "Free Estimates", to: "/contact" },
];

const stats = [
  { value: services.length, label: "Construction Services", suffix: "", to: "/services" },
  { value: siteConfig.serviceAreas.length, label: "Towns Served", suffix: "", to: "/locations" },
  { value: 10, label: "Referral Discount", suffix: "%", to: "/contact" },
];

/** Shared hover/focus treatment for the stat-bar tiles. */
const tileClass =
  "group rounded-xl transition-all duration-200 hover:bg-white/10 hover:-translate-y-0.5 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-transparent";

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <ResponsiveImage
        src="/assets/images/hero-home.jpg"
        sizes="100vw"
        alt="Paver patio completed by EC Design and Construction LLC in northern New Jersey"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchpriority="high"
        decoding="async"
        width={1500}
        height={1000}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(24,26%,7%/0.78)] via-[hsl(24,26%,7%/0.65)] to-[hsl(38,60%,12%/0.45)]" />
      <div className="absolute inset-0 gradient-mesh opacity-40" />

      {/* Decorative pulsing blobs — CSS-driven (compositor) instead of a JS
          animation loop, so they add no main-thread work / blocking time. */}
      <div
        className="hero-blob hero-blob-a absolute top-20 right-20 w-96 h-96 rounded-full hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        aria-hidden="true"
      />
      <div
        className="hero-blob hero-blob-b absolute bottom-40 left-10 w-64 h-64 rounded-full hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.4), transparent 70%)" }}
        aria-hidden="true"
      />

      <div className="relative z-10 container-max px-4 pt-20 pb-32 md:pt-24 md:pb-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <m.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <m.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mb-6 shadow-lg"
              >
                <HardHat className="h-4 w-4 text-primary" />
                <span className="text-white">
                  {siteConfig.address.city}, {siteConfig.address.state} & Northern New Jersey ·{" "}
                  {siteConfig.hours.summary}
                </span>
              </m.div>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-5 tracking-tight font-bold drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
                Remodeling &
                <br />
                <span className="text-gradient-primary">Construction</span> Done Right.
              </h1>

              {/* Readable tagline with glass background card */}
              <div className="inline-block bg-accent-dark/55 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 mb-8 max-w-xl shadow-xl">
                <p className="text-white text-base md:text-lg leading-relaxed">
                  From home remodeling and painting to masonry, roofing, flooring, pavers and new
                  construction, <span className="font-bold text-white">{siteConfig.name}</span>{" "}
                  takes on projects of every size across {siteConfig.address.city} and the
                  surrounding New Jersey communities — {siteConfig.hours.days},{" "}
                  {siteConfig.hours.time}.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-7 py-6 rounded-xl font-semibold shadow-xl"
                >
                  Get Free Estimate
                </Button>
                <Button asChild variant="outline" size="lg" className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold">
                  <a href={siteConfig.phoneHref}>
                    <Phone className="h-5 w-5" /> {siteConfig.phone}
                  </a>
                </Button>
              </div>

              <m.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <span className="text-2xl">🎁</span>
                <span className="text-white font-semibold text-sm">
                  <span className="text-primary font-bold">10% Off</span> when you refer a customer ·{" "}
                  {siteConfig.languages}
                </span>
              </m.div>
            </m.div>
          </div>

          {/* Form widget — exactly as currently positioned, right-side hero, original dimensions preserved */}
          <m.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="widget-shell bg-white">
              <div className="px-6 pt-6 pb-2">
                <h2 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  No pressure, no obligation — just an honest price in writing.
                </p>
              </div>
              <FormEmbed className="p-4 sm:p-6" defer />
            </div>
          </m.div>
        </div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 lg:mt-12 glass-card rounded-2xl p-4 md:p-5 gradient-border"
        >
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-6">
            {/* Counters */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:flex-[3]">
              {stats.map((stat, i) => (
                <m.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                >
                  <Link to={stat.to} className={`${tileClass} flex h-full flex-col items-center justify-center px-2 py-3 text-center`}>
                    <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                      <CountUp to={stat.value} />
                      <span className="text-primary">{stat.suffix}</span>
                    </div>
                    <div className="text-white/90 text-[10px] sm:text-xs font-medium uppercase tracking-wider mt-1 group-hover:text-white transition-colors">
                      {stat.label}
                    </div>
                  </Link>
                </m.div>
              ))}
            </div>

            {/* Divider: vertical beside the counters, horizontal beneath them */}
            <div className="hidden lg:block w-px self-stretch bg-white/15" aria-hidden="true" />
            <div className="lg:hidden h-px w-full bg-white/15" aria-hidden="true" />

            {/* Credibility badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:flex-[4]">
              {badges.map(({ icon: Icon, label, to }, i) => (
                <m.div
                  key={label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.05 + i * 0.1, duration: 0.5 }}
                >
                  <Link to={to} className={`${tileClass} flex h-full items-center gap-2.5 px-2.5 py-3`}>
                    <div className="h-9 w-9 rounded-lg bg-primary/25 flex items-center justify-center shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="h-4 w-4 text-primary transition-colors group-hover:text-primary-foreground" />
                    </div>
                    <span className="text-white/85 text-xs sm:text-sm font-medium leading-tight group-hover:text-white transition-colors">
                      {label}
                    </span>
                  </Link>
                </m.div>
              ))}
            </div>
          </div>
        </m.div>
      </div>

      {/* Concave/curved bottom edge */}
      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-20 md:h-24 leading-none">
        <svg
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0C240 96 480 96 720 64C960 32 1200 32 1440 64V96H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
